import { Checkbox, Form, Input, Select } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useContext, useState } from 'react';

import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { FieldValue } from 'app/components/commons/Hotel/Contacts/FieldView/FieldView';
import { SearchBox } from 'app/components/fields/SearchBox';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetHotelInfo } from 'app/hooks/data/core/admin/hotels/useGetHotelInfo';
import { ChannelManager as ChannelManagerType } from 'app/typings/channelManagers';
import type { Hotel } from 'app/typings/hotels/Hotel/Hotel';
import { AutoInventoryStatus } from 'app/utils/autoInventory/autoInventory';
import { ChannelManagerDataSource } from 'app/utils/channelManagers/channelManagers';
import { getDerbysoftSuppliersDataSource } from 'app/utils/channelManagers/derbysoft';
import { getRateModesDataSource } from 'app/utils/channelManagers/rateModes';

import { BarBBModal } from '../../../Form/Communication/_components/BarBBModal';
import styles from '../General.module.scss';
import type { GeneralSectionProps } from '../GeneralSection';

import { CmConfirmModal } from './CmConfirmModal';
import { SelectValue } from './SelectValue';

const RULES = [{ required: true }];

const AUTO_INVENTORY_OPTIONS = [
  { id: true, name: AutoInventoryStatus.Activated },
  { id: false, name: AutoInventoryStatus.Deactivated },
];

const BarBbValue = ({ checked }: { checked?: boolean }) => (
  <FieldValue value={checked ? 'Yes' : 'No'} />
);

export const Connectivity = ({ hotelId }: GeneralSectionProps) => {
  const { isEditMode } = useContext(ViewEditModeContext);

  const form = Form.useFormInstance<Hotel>();
  const channelManager = Form.useWatch('channelManager', form);
  const stockScrapperEnabled = Form.useWatch('stockScrapperEnabled', form);
  const { data: hotel } = useGetHotelInfo(hotelId);

  const [openBarBbModal, setOpenBarBbModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const hasChannelManager =
    !!channelManager && channelManager !== ChannelManagerType.NONE;

  const handleBarBbChange = (e: CheckboxChangeEvent) => {
    if (e.target.checked && isEditMode) {
      setOpenBarBbModal(true);
      // Prevent the checkbox from being checked until the user confirms.
      setTimeout(() => {
        form.setFieldValue('connectedBarBb', false);
      }, 0);
    }
  };

  return (
    <>
      <FormLineLayout
        label="Channel manager"
        isRequired
        rightClassName={styles.selectField}
      >
        <Form.Item name="channelManager" rules={isEditMode ? RULES : undefined}>
          {isEditMode ? (
            <SearchBox
              dataSource={ChannelManagerDataSource}
              onChange={(value: ChannelManagerType) =>
                stockScrapperEnabled &&
                value !== ChannelManagerType.NONE &&
                setOpenConfirmModal(true)
              }
            />
          ) : (
            <SelectValue options={ChannelManagerDataSource} />
          )}
        </Form.Item>
      </FormLineLayout>
      {hasChannelManager && (
        <FormLineLayout
          label="Connected rates"
          isRequired
          rightClassName={styles.selectField}
        >
          <Form.Item name="rateMode" rules={isEditMode ? RULES : undefined}>
            {isEditMode ? (
              <SearchBox dataSource={getRateModesDataSource(hotel?.isLegacy)} />
            ) : (
              <SelectValue options={getRateModesDataSource(hotel?.isLegacy)} />
            )}
          </Form.Item>
        </FormLineLayout>
      )}
      {hasChannelManager && channelManager === ChannelManagerType.MEWS && (
        <FormLineLayout label="CM Credentials" isRequired>
          <Form.Item
            name="channelManagerCredentials"
            rules={isEditMode ? RULES : undefined}
          >
            {isEditMode ? <Input /> : <FieldValue />}
          </Form.Item>
        </FormLineLayout>
      )}
      {hasChannelManager && channelManager === ChannelManagerType.DERBYSOFT && (
        <FormLineLayout
          label="Supplier"
          isRequired
          rightClassName={styles.selectField}
        >
          <Form.Item name="supplierId" rules={isEditMode ? RULES : undefined}>
            {isEditMode ? (
              <Select showSearch>
                {getDerbysoftSuppliersDataSource().map((supplier) => (
                  <Select.Option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <SelectValue options={getDerbysoftSuppliersDataSource()} />
            )}
          </Form.Item>
        </FormLineLayout>
      )}
      {!hasChannelManager && (
        <FormLineLayout
          label="Auto inventory"
          rightClassName={styles.selectField}
        >
          <Form.Item name="stockScrapperEnabled">
            {isEditMode ? (
              <Select disabled showArrow={false}>
                <Select.Option value={true}>
                  {AutoInventoryStatus.Activated}
                </Select.Option>
                <Select.Option value={false}>
                  {AutoInventoryStatus.Deactivated}
                </Select.Option>
              </Select>
            ) : (
              <SelectValue options={AUTO_INVENTORY_OPTIONS} />
            )}
          </Form.Item>
        </FormLineLayout>
      )}
      <FormLineLayout label="Includes breakfast (BAR BB)">
        <Form.Item name="connectedBarBb" valuePropName="checked">
          {isEditMode ? (
            <Checkbox onChange={handleBarBbChange} />
          ) : (
            <BarBbValue />
          )}
        </Form.Item>
      </FormLineLayout>

      <CmConfirmModal
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        form={form}
      />
      <BarBBModal
        openBarBbModal={openBarBbModal}
        setOpenBarBbModal={setOpenBarBbModal}
        values={form.getFieldsValue(true)}
        setFieldsValue={form.setFieldsValue}
      />
    </>
  );
};
